import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import {
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Info from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import CopyrightIcon from '@mui/icons-material/Copyright';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import '../shared.css';

function StockIn() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const history = useHistory();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];
    const getFormattedStartDate = () => {
        const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
    };

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchtableData();
    }, [isAuthenticated, companyId, branchId, token, logout]);

    const fetchtableData = async () => {
        setData([]);
        try {
            const response = await fetchData('StockTransferApi', `/stockInDetails/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching StockIn Details:', error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        }
    };

    const filtered = data.filter(item =>
        Object.values(item).some(v => String(v).toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const handleDelete = async () => {
        deleteData('StockTransferApi', `${deleteItemId}`, token)
            .then(() => { fetchtableData(); setDeleteItemId(null); setConfirmDialogOpen(false); })
            .catch(err => console.error('Error deleting record:', err));
    };

    const handleActivate = (itemId) => {
        activateData('StockTransferApi', `${itemId}`, token)
            .then(() => fetchtableData())
            .catch(err => console.error('Error activating record:', err));
    };

    function formatDate(dateString) {
        return new Date(dateString).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });
    }

    const statusBadge = (item) => {
        if (!item.IsActive) return <span style={{ color: '#94a3b8', fontWeight: 700, fontSize: 11 }}>Inactive</span>;
        if (item.InputRejected) return <span style={{ color: '#dc2626', fontWeight: 700, fontSize: 11 }}>Rejected</span>;
        if (item.InputAccepted) return <span style={{ color: '#16a34a', fontWeight: 700, fontSize: 11 }}>Approved</span>;
        return <span style={{ color: '#b45309', fontWeight: 700, fontSize: 11 }}>Waiting for Approval</span>;
    };

    const rowBg = (item) => {
        if (!item.IsActive) return { opacity: 0.5 };
        if (item.InputAccepted) return { background: '#d4edda' };
        if (item.InputRejected) return { background: '#f8d7da' };
        return { background: '#D9C9F7' };
    };

    return (
        <div className="pg-container">
            {/* ── Header ── */}
            <div className="pg-header">
                <h5 className="pg-title">Stock Transfer In</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
                            <input className="pg-input" type="date" value={fromDate}
                                onChange={e => setFromDate(e.target.value)} />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
                            <input className="pg-input" type="date" value={toDate}
                                onChange={e => setToDate(e.target.value)} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={fetchtableData} title="Refresh">
                            <RefreshIcon /> Refresh
                        </button>
                    </div>

                    <div className="pg-action-row">
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearch(v => !v)}>
                            <SearchIcon /> Search
                        </button>
                        
                        <Link to="/StockInCreate" className="pg-btn pg-btn-primary">
                            <AddIcon /> Create New
                        </Link>

                    </div>

                    {showSearch && (
                        <input className="pg-input pg-search-input" placeholder="Search…"
                            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus />
                    )}
                </div>
            </div>

            {/* ── Table ── */}
            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Bill Date</th>
                            <th>Bill Number</th>
                            <th>From Branch</th>
                            <th style={{ textAlign: 'right' }}>Amount</th>
                            <th style={{ textAlign: 'right' }}>Quantity</th>
                            <th style={{ textAlign: 'right' }}>Status / Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0
                            ? <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: 16 }}>No records found</td></tr>
                            : filtered.map((item, index) => (
                                <tr key={item.Id} style={rowBg(item)}>
                                    <td>{index + 1}</td>
                                    <td>{formatDate(item.BillDate)}</td>
                                    <td>{item.BillPrefix}/S/{item.BillNumber}</td>
                                    <td>{item.ToBranchName}</td>
                                    <td style={{ textAlign: 'right' }}>{item.GrandTotal != null ? item.GrandTotal.toFixed(2) : '0.00'}</td>
                                    <td style={{ textAlign: 'right' }}>{item.TotalQuantity}</td>
                                    <td className="actions-cell">
                                        <Link to={`/StockInDetail/${item.Id}`}>
                                            <IconButton size="small" title="Details">
                                                <Info style={{ color: item.InputRejected ? '#dc2626' : item.InputAccepted ? '#b8935a' : '#1c2a3a', fontSize: 17 }} />
                                            </IconButton>
                                        </Link>
                                        {statusBadge(item)}
                                    </td>
                                </tr>
                            ))
                        }
                    </tbody>
                </table>
            </div>

            {/* ── Delete Confirm Dialog ── */}
            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                <DialogTitle>Delete Record?</DialogTitle>
                <DialogContent><DialogContentText>Are you sure you want to delete this record?</DialogContentText></DialogContent>
                <DialogActions>
                    <button className="pg-btn pg-btn-danger" onClick={() => setConfirmDialogOpen(false)}>Cancel</button>
                    <button className="pg-btn pg-btn-success" onClick={handleDelete}>Confirm</button>
                </DialogActions>
            </Dialog>

            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default StockIn;
